<template>
  <section class="inspection-dashboard">
    <header class="dashboard-heading">
      <div>
        <h1>Gestão de campo</h1>
        <p>Acompanhe as visitas técnicas e os resultados comerciais.</p>
      </div>
      <label class="month-filter">
        <span>Período</span>
        <input type="month" v-model="selectedMonth" @change="loadDashboard" />
      </label>
    </header>

    <div class="summary-grid">
      <article v-for="card in summaryCards" :key="card.key" class="summary-card">
        <span class="summary-icon"><b-icon :icon="card.icon" /></span>
        <div class="summary-content">
          <small>{{ card.label }}</small>
          <span v-if="loading" class="skeleton skeleton-value"></span>
          <strong v-else>{{ formatCardValue(card) }}</strong>
          <span
            v-if="!loading"
            :class="['summary-trend', card.change >= 0 ? 'positive' : 'negative']"
          >
            <b-icon :icon="card.change >= 0 ? 'arrow-up' : 'arrow-down'" />
            {{ formatChange(card) }} vs mês anterior
          </span>
          <span v-else class="skeleton skeleton-line"></span>
        </div>
      </article>
    </div>

    <div class="dashboard-grid">
      <article class="dashboard-panel ranking-panel">
        <div class="panel-title">
          <strong>Ranking de responsáveis</strong>
          <span>{{ periodLabel }}</span>
        </div>
        <div v-if="loading" class="panel-skeleton">
          <span v-for="index in 5" :key="index" class="skeleton skeleton-row"></span>
        </div>
        <div v-else-if="dashboard.ranking.length" class="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Responsável</th>
                <th>Visitas</th>
                <th>Pedidos</th>
                <th>Conversão</th>
                <th>Vendas</th>
                <th>Ticket médio</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(person, index) in dashboard.ranking"
                :key="person.id || `${person.name}-${index}`"
              >
                <td><span class="avatar">{{ initials(person.name) }}</span>{{ person.name }}</td>
                <td>{{ person.visits }}</td>
                <td>{{ person.orders }}</td>
                <td class="highlight">{{ formatPercent(person.conversion) }}</td>
                <td>{{ formatCurrency(person.sales) }}</td>
                <td>{{ formatCurrency(person.averageTicket) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-panel">Nenhuma visita no período.</div>
      </article>

      <article class="dashboard-panel funnel-panel">
        <div class="panel-title"><strong>Funil das visitas</strong></div>
        <div v-if="loading" class="panel-skeleton tall">
          <span v-for="index in 5" :key="index" class="skeleton skeleton-funnel"></span>
        </div>
        <div v-else class="funnel-list">
          <div v-for="(item, index) in dashboard.funnel" :key="item.label" class="funnel-row">
            <div class="funnel-stage-wrap">
              <div
                :class="['funnel-bar', { tail: index === dashboard.funnel.length - 1 }]"
                :style="{ width: funnelWidth(index), background: funnelColors[index] }"
              >
                {{ item.currency ? formatCurrency(item.value) : item.value }}
              </div>
            </div>
            <span>{{ item.label }}</span>
          </div>
        </div>
      </article>

      <article class="dashboard-panel locations-panel">
        <div class="panel-title">
          <strong>Localização das visitas</strong>
          <span>{{ mappedVisitCount }} visitas com coordenadas</span>
        </div>
        <div v-if="loading" class="map-placeholder"><span class="skeleton map-skeleton"></span></div>
        <InspectionVisitMap
          v-else-if="dashboard.locations.length"
          :locations="dashboard.locations"
        />
        <div v-else class="empty-panel">As visitas deste mês não possuem coordenadas.</div>
        <div class="map-legend">
          <span><i class="dot completed"></i> Concluída</span>
          <span><i class="dot progress"></i> Em andamento</span>
          <span><i class="dot open"></i> Aberta</span>
        </div>
      </article>

      <article class="dashboard-panel reasons-panel">
        <div class="panel-title"><strong>Motivos das visitas</strong></div>
        <div v-if="loading" class="panel-skeleton centered">
          <span class="skeleton donut-skeleton"></span>
        </div>
        <div v-else-if="dashboard.reasons.length" class="donut-layout">
          <div class="donut" :style="{ background: reasonsGradient }">
            <span>{{ totalReasons }}</span>
          </div>
          <div class="legend-list">
            <span
              v-for="(reason, index) in dashboard.reasons"
              :key="reason.id || `${reason.name}-${index}`"
            >
              <i :style="{ background: chartColors[index % chartColors.length] }"></i>
              <b>{{ reason.name }}</b>
              <em>{{ reasonPercent(reason.total) }}%</em>
            </span>
          </div>
        </div>
        <div v-else class="empty-panel">Nenhum motivo informado.</div>
      </article>

      <article class="dashboard-panel performance-panel">
        <div class="panel-title"><strong>Visitas x pedidos por responsável</strong></div>
        <div v-if="loading" class="panel-skeleton">
          <span v-for="index in 5" :key="index" class="skeleton skeleton-row"></span>
        </div>
        <div v-else-if="dashboard.ranking.length" class="bar-chart">
          <div
            v-for="(person, index) in dashboard.ranking.slice(0, 6)"
            :key="person.id || `${person.name}-${index}`"
            class="bar-group"
          >
            <span>{{ firstName(person.name) }}</span>
            <div class="bars">
              <i class="visits-bar" :style="{ width: barWidth(person.visits, maxVisits) }">
                <b>{{ person.visits }}</b>
              </i>
              <i class="orders-bar" :style="{ width: barWidth(person.orders, maxVisits) }">
                <b>{{ person.orders }}</b>
              </i>
            </div>
          </div>
          <div class="bar-legend"><span><i class="visits"></i>Visitas</span><span><i class="orders"></i>Pedidos</span></div>
        </div>
        <div v-else class="empty-panel">Sem dados para comparar.</div>
      </article>

      <article class="dashboard-panel sales-panel">
        <div class="panel-title"><strong>Vendas originadas por responsável</strong></div>
        <div v-if="loading" class="panel-skeleton">
          <span v-for="index in 5" :key="index" class="skeleton skeleton-row"></span>
        </div>
        <div v-else-if="dashboard.ranking.length" class="sales-bars">
          <div
            v-for="(person, index) in dashboard.ranking.slice(0, 6)"
            :key="person.id || `${person.name}-${index}`"
          >
            <span>{{ firstName(person.name) }}</span>
            <i :style="{ width: barWidth(person.sales, maxSales) }"></i>
            <b>{{ formatCurrency(person.sales) }}</b>
          </div>
        </div>
        <div v-else class="empty-panel">Nenhuma venda originada no período.</div>
      </article>

      <article class="dashboard-panel cultures-panel">
        <div class="panel-title"><strong>Culturas mais atendidas</strong></div>
        <div v-if="loading" class="panel-skeleton">
          <span v-for="index in 5" :key="index" class="skeleton skeleton-row"></span>
        </div>
        <div v-else-if="dashboard.cultures.length" class="rank-bars">
          <div
            v-for="(culture, index) in dashboard.cultures"
            :key="culture.id || `${culture.name}-${index}`"
          >
            <span>{{ culture.name }}</span>
            <i :style="{ width: barWidth(culture.total, maxCultures) }"></i>
            <b>{{ culture.total }}</b>
          </div>
        </div>
        <div v-else class="empty-panel">Nenhuma cultura informada.</div>
      </article>

      <article class="dashboard-panel mileage-panel">
        <div class="panel-title">
          <strong><b-icon-speedometer2 /> Quilometragem das visitas</strong>
          <span v-if="!mileageLoading && !mileageError">
            {{ mileage.visitsCalculated }} rotas calculadas
          </span>
        </div>
        <div v-if="mileageLoading" class="mileage-loading">
          <div class="mileage-metrics">
            <span v-for="index in 4" :key="index" class="skeleton mileage-skeleton"></span>
          </div>
          <div class="panel-skeleton"><span class="skeleton skeleton-row"></span><span class="skeleton skeleton-row"></span></div>
        </div>
        <div v-else-if="mileageError" class="empty-panel compact">
          {{ mileageError }}
        </div>
        <div v-else class="mileage-content">
          <div class="mileage-metrics">
            <div>
              <span class="metric-icon"><b-icon-speedometer /></span>
              <small>Distância percorrida</small>
              <strong>{{ formatKm(mileage.totalKm) }}</strong>
            </div>
            <div>
              <span class="metric-icon"><b-icon-graph-up /></span>
              <small>Média por visita</small>
              <strong>{{ formatKm(mileage.averageKmPerVisit) }}</strong>
            </div>
            <div>
              <span class="metric-icon"><b-icon-cash-stack /></span>
              <small>Vendido por km</small>
              <strong>{{ formatCurrency(revenuePerKm) }}</strong>
            </div>
            <div>
              <span class="metric-icon"><b-icon-truck /></span>
              <small>Veículos utilizados</small>
              <strong>{{ mileage.vehiclesUsed }}</strong>
            </div>
          </div>
          <div v-if="mileage.byProfessional.length" class="mileage-table-wrap">
            <table class="mileage-table">
              <thead><tr><th>Responsável</th><th>Veículo</th><th>Km percorridos</th><th>Visitas</th><th>Km/visita</th></tr></thead>
              <tbody>
                <tr v-for="person in mileage.byProfessional" :key="person.id">
                  <td>{{ person.name }}</td>
                  <td>{{ person.vehicles.join(", ") }}</td>
                  <td>{{ formatKm(person.totalKm) }}</td>
                  <td>{{ person.visits }}</td>
                  <td>{{ formatKm(person.averageKmPerVisit) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-inline">Nenhuma visita com veículo e rota válida no período.</div>
          <small v-if="mileage.visitsWithoutRoute" class="route-note">
            {{ mileage.visitsWithoutRoute }} visita(s) com veículo ficaram sem rota calculável.
          </small>
        </div>
      </article>

      <article class="dashboard-panel pending-panel">
        <div class="panel-title">
          <strong><b-icon-bell-fill /> Acompanhamentos pendentes</strong>
          <span class="pending-count">{{ dashboard.pending.length }}</span>
        </div>
        <div v-if="loading" class="panel-skeleton">
          <span v-for="index in 4" :key="index" class="skeleton skeleton-row"></span>
        </div>
        <div v-else-if="dashboard.pending.length" class="pending-list">
          <div v-for="item in dashboard.pending.slice(0, 8)" :key="`${item.inspectionId}-${item.id}`">
            <span class="pending-icon"><b-icon-clipboard-check /></span>
            <span><b>{{ item.property }}</b><small>{{ item.description }}</small></span>
            <span><b>{{ item.responsible }}</b><small>{{ formatDate(item.deadline) }}</small></span>
            <em>{{ item.status }}</em>
          </div>
        </div>
        <div v-else class="empty-panel">Nenhum acompanhamento pendente.</div>
      </article>
    </div>
  </section>
</template>

<script>
import { DateTime } from "luxon";
import serviceInspection from "../../services/serviceInspection";
import InspectionVisitMap from "./InspectionVisitMap.vue";

const emptyDashboard = () => ({
  summary: {
    visits: { value: 0, change: 0 },
    producers: { value: 0, change: 0 },
    orders: { value: 0, change: 0 },
    conversion: { value: 0, change: 0 },
    sales: { value: 0, change: 0 },
    averageTicket: { value: 0, change: 0 },
  },
  ranking: [],
  funnel: [],
  locations: [],
  reasons: [],
  cultures: [],
  statuses: [],
  pending: [],
  vehiclesUsed: 0,
});

const emptyMileage = () => ({
  totalKm: 0,
  averageKmPerVisit: 0,
  visitsCalculated: 0,
  visitsWithoutRoute: 0,
  vehiclesUsed: 0,
  byProfessional: [],
});

export default {
  name: "InspectionDashboard",
  components: { InspectionVisitMap },
  data() {
    return {
      selectedMonth: DateTime.local().toFormat("yyyy-MM"),
      loading: true,
      mileageLoading: true,
      mileageError: "",
      dashboard: emptyDashboard(),
      mileage: emptyMileage(),
      funnelColors: ["#4c86cc", "#36a2b4", "#37ad75", "#68bb3e", "#00855d"],
      chartColors: ["#087a4e", "#12a365", "#6fc13f", "#f5b82e", "#2c91b5", "#aab4b0"],
    };
  },
  computed: {
    summaryCards() {
      const summary = this.dashboard.summary;
      return [
        { key: "visits", label: "Visitas realizadas", icon: "people", ...summary.visits },
        { key: "producers", label: "Produtores visitados", icon: "person-fill", ...summary.producers },
        { key: "orders", label: "Pedidos gerados", icon: "cart3", ...summary.orders },
        { key: "conversion", label: "Conversão", icon: "percent", ...summary.conversion, percent: true, points: true },
        { key: "sales", label: "Vendas originadas das visitas", icon: "cash-stack", ...summary.sales, currency: true },
        { key: "averageTicket", label: "Ticket médio", icon: "receipt", ...summary.averageTicket, currency: true },
      ];
    },
    periodLabel() {
      const period = DateTime.fromFormat(this.selectedMonth, "yyyy-MM");
      return period.isValid
        ? period.setLocale("pt-BR").toFormat("MMMM 'de' yyyy")
        : "Período mensal";
    },
    totalReasons() {
      return this.dashboard.reasons.reduce((total, item) => total + item.total, 0);
    },
    reasonsGradient() {
      if (!this.totalReasons) return "#e7eeeb";
      let cursor = 0;
      const stops = this.dashboard.reasons.map((reason, index) => {
        const start = cursor;
        cursor += (reason.total / this.totalReasons) * 100;
        return `${this.chartColors[index % this.chartColors.length]} ${start}% ${cursor}%`;
      });
      return `conic-gradient(${stops.join(", ")})`;
    },
    maxVisits() {
      return Math.max(...this.dashboard.ranking.map(item => item.visits), 1);
    },
    maxSales() {
      return Math.max(...this.dashboard.ranking.map(item => item.sales), 1);
    },
    maxCultures() {
      return Math.max(...this.dashboard.cultures.map(item => item.total), 1);
    },
    mappedVisitCount() {
      return new Set(
        this.dashboard.locations.map(item => item.inspectionId || item.id)
      ).size;
    },
    revenuePerKm() {
      if (!this.mileage.totalKm) return 0;
      return Number(this.dashboard.summary.sales.value || 0) / this.mileage.totalKm;
    },
  },
  created() {
    this.loadDashboard();
  },
  methods: {
    async loadDashboard() {
      if (!this.selectedMonth) return;
      const requestedMonth = this.selectedMonth;
      this.loadMileage(requestedMonth);
      this.loading = true;
      try {
        const data = await serviceInspection.dashboard(requestedMonth);
        if (this.selectedMonth !== requestedMonth) return;
        this.dashboard = { ...emptyDashboard(), ...data };
      } catch (error) {
        if (this.selectedMonth !== requestedMonth) return;
        this.dashboard = emptyDashboard();
        this.$toast.open({
          message: "Não foi possível carregar o dashboard de visitas",
          type: "error",
        });
      } finally {
        if (this.selectedMonth === requestedMonth) this.loading = false;
      }
    },
    async loadMileage(month) {
      this.mileageLoading = true;
      this.mileageError = "";
      this.mileage = emptyMileage();
      try {
        const data = await serviceInspection.dashboardMileage(month);
        if (this.selectedMonth !== month) return;
        this.mileage = { ...emptyMileage(), ...data };
      } catch (error) {
        if (this.selectedMonth !== month) return;
        this.mileageError =
          error?.response?.data?.message ||
          "Não foi possível calcular as rotas deste período.";
      } finally {
        if (this.selectedMonth === month) this.mileageLoading = false;
      }
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      });
    },
    formatPercent(value) {
      return `${Number(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
    },
    formatCardValue(card) {
      if (card.currency) return this.formatCurrency(card.value);
      if (card.percent) return this.formatPercent(card.value);
      return Number(card.value || 0).toLocaleString("pt-BR");
    },
    formatChange(card) {
      const value = Math.abs(Number(card.change || 0));
      return `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}${card.points ? " p.p." : "%"}`;
    },
    formatDate(value) {
      if (!value) return "Sem prazo";
      const date = DateTime.fromISO(String(value));
      return date.isValid ? `Prazo: ${date.toFormat("dd/MM/yyyy")}` : "Sem prazo";
    },
    initials(name) {
      return String(name || "NI").split(" ").filter(Boolean).slice(0, 2).map(part => part[0]).join("").toUpperCase();
    },
    firstName(name) {
      return String(name || "Não informado").split(" ")[0];
    },
    reasonPercent(total) {
      return this.totalReasons ? Math.round((total / this.totalReasons) * 100) : 0;
    },
    barWidth(value, maximum) {
      return `${Math.max((Number(value || 0) / maximum) * 100, value ? 4 : 0)}%`;
    },
    funnelWidth(index) {
      return `${Math.max(100 - index * 16, 36)}%`;
    },
    formatKm(value) {
      return `${Number(value || 0).toLocaleString("pt-BR", {
        maximumFractionDigits: 1,
      })} km`;
    },
  },
};
</script>

<style scoped>
.inspection-dashboard { flex: 1; min-height: 0; overflow-y: auto; padding: 10px 2px 32px; color: #17201e; }
.dashboard-heading { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 14px; }
.dashboard-heading h1 { margin: 0; font-size: 23px; font-weight: 700; }
.dashboard-heading p { margin: 4px 0 0; color: #687773; font-size: 13px; }
.month-filter { min-width: 205px; margin: 0; padding: 7px 10px; background: white; border: 1px solid #dfe7e4; border-radius: 7px; }
.month-filter span { display: block; font-size: 11px; font-weight: 600; color: #66736f; }
.month-filter input { width: 100%; height: 25px; border: 0; padding: 0; color: #26312e; font-size: 13px !important; }
.summary-grid { display: grid; grid-template-columns: repeat(6, minmax(150px, 1fr)); gap: 10px; margin-bottom: 10px; }
.summary-card, .dashboard-panel { background: #fff; border: 1px solid #e2e9e7; border-radius: 9px; box-shadow: 0 2px 7px rgba(18, 58, 48, .035); }
.summary-card { min-height: 108px; padding: 15px; display: flex; align-items: flex-start; gap: 11px; }
.summary-icon { flex: 0 0 40px; height: 40px; display: grid; place-items: center; border-radius: 50%; background: #edf7f1; color: #087a4e; font-size: 19px; }
.summary-content { min-width: 0; display: flex; flex-direction: column; }
.summary-content small { min-height: 30px; font-size: 11px; line-height: 1.3; }
.summary-content strong { font-size: 21px; line-height: 1.2; white-space: nowrap; }
.summary-trend { margin-top: 7px; font-size: 10px; white-space: nowrap; color: #687773; }
.summary-trend.positive svg { color: #159455; }
.summary-trend.negative svg { color: #d44f47; }
.dashboard-grid { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); gap: 10px; }
.dashboard-panel { min-height: 0; padding: 15px; overflow: hidden; }
.ranking-panel, .locations-panel { grid-column: span 7; min-height: 300px; }
.funnel-panel, .reasons-panel { grid-column: span 5; min-height: 300px; }
.performance-panel, .sales-panel, .cultures-panel { grid-column: span 4; min-height: 190px; }
.mileage-panel { grid-column: span 8; min-height: 260px; }
.pending-panel { grid-column: span 4; min-height: 260px; }
.panel-title { min-height: 28px; display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; font-size: 13px; }
.panel-title > span { color: #7b8884; font-size: 11px; text-transform: capitalize; }
.table-scroll { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 11px; }
th { color: #60706b; font-size: 10px; font-weight: 600; text-align: left; padding: 7px 5px; border-bottom: 1px solid #e4eae8; }
td { padding: 8px 5px; border-bottom: 1px solid #eef2f1; white-space: nowrap; }
td:first-child { display: flex; align-items: center; gap: 6px; font-weight: 600; }
.avatar { width: 27px; height: 27px; display: grid; place-items: center; border-radius: 50%; background: #dcede7; color: #087a4e; font-size: 10px; }
.highlight { color: #0a8b52; font-weight: 700; }
.funnel-list { padding: 4px 0; }
.funnel-row { display: grid; grid-template-columns: minmax(150px, 1fr) 145px; align-items: center; gap: 12px; }
.funnel-stage-wrap { height: 42px; display: flex; justify-content: center; }
.funnel-bar { height: 43px; display: grid; place-items: center; color: white; font-size: 11px; font-weight: 700; clip-path: polygon(0 0, 100% 0, 91% 100%, 9% 100%); transition: width .25s ease; }
.funnel-bar.tail { height: 35px; margin-top: 1px; clip-path: none; border-radius: 0 0 3px 3px; }
.funnel-row > span { font-size: 10px; color: #33403c; }
.map-placeholder { position: relative; height: 240px; overflow: hidden; border-radius: 7px; background-color: #eef2eb; }
.completed { background: #14a363; }.progress { background: #2f72bd; }.open { background: #e7ae25; }
.map-legend { display: flex; gap: 16px; padding-top: 8px; color: #687773; font-size: 10px; }
.dot { display: inline-block; width: 7px; height: 7px; border-radius: 50%; margin-right: 3px; }
.donut-layout { min-height: 220px; display: flex; align-items: center; justify-content: center; gap: 18px; }
.donut { width: 125px; height: 125px; flex: 0 0 125px; border-radius: 50%; display: grid; place-items: center; position: relative; }
.donut:after { content: ''; width: 65px; height: 65px; position: absolute; border-radius: 50%; background: white; }
.donut span { z-index: 1; font-size: 16px; font-weight: 700; }
.legend-list { min-width: 0; display: flex; flex-direction: column; gap: 7px; }
.legend-list span { display: grid; grid-template-columns: 8px minmax(70px, 1fr) 34px; align-items: center; gap: 6px; font-size: 10px; }
.legend-list i { width: 7px; height: 7px; border-radius: 50%; }
.legend-list b { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }
.legend-list em { text-align: right; font-style: normal; }
.bar-chart, .sales-bars, .rank-bars { padding-top: 3px; }
.bar-group { display: grid; grid-template-columns: 95px 1fr; align-items: center; gap: 8px; margin-bottom: 10px; font-size: 10px; }
.bars { display: flex; flex-direction: column; gap: 2px; }
.bars i { display: block; min-width: 1px; height: 7px; border-radius: 0 4px 4px 0; position: relative; }
.bars b { position: absolute; left: calc(100% + 4px); top: -3px; font-size: 9px; font-style: normal; }
.visits-bar { background: #087a4e; }.orders-bar { background: #77bd45; }
.bar-legend { display: flex; justify-content: center; gap: 16px; font-size: 10px; }
.bar-legend i { display: inline-block; width: 8px; height: 5px; margin-right: 3px; }.bar-legend .visits { background: #087a4e; }.bar-legend .orders { background: #77bd45; }
.sales-bars > div, .rank-bars > div { display: grid; grid-template-columns: 105px minmax(30px, 1fr) 105px; align-items: center; gap: 8px; min-height: 31px; font-size: 10px; }
.sales-bars i, .rank-bars i { display: block; height: 7px; border-radius: 0 5px 5px 0; background: linear-gradient(90deg, #0b7049, #19965e); }
.sales-bars b, .rank-bars b { font-size: 10px; font-weight: 500; }
.rank-bars > div { grid-template-columns: 95px minmax(30px, 1fr) 30px; }
.mileage-content { display: flex; flex-direction: column; gap: 10px; }
.mileage-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
.mileage-metrics > div { min-height: 76px; display: grid; grid-template-columns: 30px 1fr; grid-template-rows: auto auto; column-gap: 8px; align-items: center; padding: 10px; border: 1px solid #e3ebe8; border-radius: 7px; background: #f8fbfa; }
.mileage-metrics .metric-icon { grid-row: 1 / 3; width: 30px; height: 30px; display: grid; place-items: center; border-radius: 50%; background: #e4f3ec; color: #087a4e; font-size: 15px; }
.mileage-metrics small { align-self: end; color: #687773; font-size: 9px; white-space: nowrap; }
.mileage-metrics strong { align-self: start; color: #17201e; font-size: 14px; white-space: nowrap; }
.mileage-skeleton { height: 76px; }
.mileage-table-wrap { max-height: 180px; overflow: auto; }
.mileage-table td:first-child { display: table-cell; }
.mileage-table th, .mileage-table td { padding: 6px 7px; }
.empty-inline { padding: 22px 10px; color: #7d8985; font-size: 11px; text-align: center; }
.route-note { color: #96701b; font-size: 9px; }
.pending-count { width: 20px; height: 20px; display: grid; place-items: center; color: white !important; background: #db2936; border-radius: 50%; font-weight: 700; }
.pending-list > div { display: grid; grid-template-columns: 28px 1.2fr 1fr auto; gap: 8px; align-items: center; padding: 9px 0; border-bottom: 1px solid #edf1ef; font-size: 10px; }
.pending-list span { display: flex; flex-direction: column; min-width: 0; }.pending-list b, .pending-list small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pending-list small { color: #76827f; }.pending-list em { padding: 4px 7px; border-radius: 4px; background: #fff1c4; color: #8e6400; font-style: normal; font-weight: 600; white-space: nowrap; }
.pending-icon { color: #078256; font-size: 14px; }
.empty-panel { min-height: 160px; display: grid; place-items: center; color: #7d8985; font-size: 12px; }
.empty-panel.compact { min-height: 220px; text-align: center; }
.panel-skeleton { display: flex; flex-direction: column; gap: 10px; padding-top: 6px; }.panel-skeleton.centered { align-items: center; }.panel-skeleton.tall { align-items: center; }
.skeleton { display: block; overflow: hidden; position: relative; border-radius: 4px; background: #e7edeb; }
.skeleton:after { content: ''; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,.7), transparent); animation: shimmer 1.25s infinite; }
.skeleton-value { width: 75%; height: 21px; }.skeleton-line { width: 100%; height: 8px; margin-top: 7px; }.skeleton-row { width: 100%; height: 23px; }.skeleton-funnel { width: 75%; height: 26px; }.skeleton-funnel:nth-child(2) { width: 66%; }.skeleton-funnel:nth-child(3) { width: 58%; }.skeleton-funnel:nth-child(4) { width: 50%; }.skeleton-funnel:nth-child(5) { width: 42%; }
.map-skeleton { width: 100%; height: 100%; }.donut-skeleton { width: 125px; height: 125px; border-radius: 50%; }
@keyframes shimmer { to { transform: translateX(100%); } }
@media (max-width: 1250px) { .summary-grid { grid-template-columns: repeat(3, 1fr); }.mileage-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 1100px) { .ranking-panel, .funnel-panel, .locations-panel, .reasons-panel, .performance-panel, .sales-panel, .cultures-panel, .mileage-panel, .pending-panel { grid-column: span 6; } }
@media (max-width: 850px) { .summary-grid { grid-template-columns: repeat(2, 1fr); }.dashboard-heading { align-items: stretch; gap: 10px; }.dashboard-panel { grid-column: 1 / -1; }.dashboard-heading { flex-direction: column; }.month-filter { min-width: 0; }.pending-list > div { grid-template-columns: 24px 1fr auto; }.pending-list > div > span:nth-child(3) { display: none; } }
@media (max-width: 560px) { .mileage-metrics { grid-template-columns: 1fr; }.funnel-row { grid-template-columns: minmax(125px, 1fr) 115px; } }
</style>
